import { computeTextBannerLayout } from "@/lib/drawTextBanner";
import { buildFontString, measureTextLabelTextHeight } from "@/lib/drawTextLabels";
import { getBroadcastMapChange } from "@/state/realtimeBroadcast";
import { useEditorStore } from "@/state/useEditorStore";
import { useMapStore } from "@/state/useMapStore";
import { useTextLabelStore } from "@/state/useTextLabelStore";
import {
  getTitleBannerImage,
  preloadTitleBannerImages,
  TITLE_BANNER_FILENAMES,
  type MapTextLabel,
} from "@west-marches/shared";
import { useCallback, useEffect, useRef, useState } from "react";

export const EDITOR_HORIZONTAL_NUDGE_FACTOR = 0.117;
export const EDITOR_VERTICAL_NUDGE_FACTOR = 0.163;
const MAX_TEXT_LABEL_LINES = 2;

/** Caps text at `MAX_TEXT_LABEL_LINES` without touching trailing newlines, so
 *  pressing Enter to start a fresh empty line doesn't fight the browser's
 *  contentEditable cursor placement. Use this during live editing. */
function enforceTextLabelLineCap(text: string): string {
  const lines = text.split("\n");
  if (lines.length <= MAX_TEXT_LABEL_LINES) return text;
  return lines.slice(0, MAX_TEXT_LABEL_LINES).join("\n");
}

/** Final cleanup before persisting: drop trailing blank lines and cap. */
function normalizeTextLabelEditorText(text: string): string {
  return enforceTextLabelLineCap(text.replace(/\n+$/, ""));
}

interface TextLabelEditorProps {
  cameraRef: React.MutableRefObject<{ x: number; y: number; zoom: number }>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
}

export function TextLabelEditor({ cameraRef, canvasRef }: TextLabelEditorProps) {
  const editingLabelId = useTextLabelStore((s) => s.editingLabelId);
  const editPosition = useTextLabelStore((s) => s.editPosition);
  const pendingStyle = useTextLabelStore((s) => s.pendingStyle);
  const commitEdit = useTextLabelStore((s) => s.commitEdit);
  const cancelEdit = useTextLabelStore((s) => s.cancelEdit);
  const setPendingStyle = useTextLabelStore((s) => s.setPendingStyle);
  const mapState = useMapStore((s) => s.mapState);
  const applyMapChanges = useMapStore((s) => s.applyMapChanges);
  const activeTool = useEditorStore((s) => s.activeTool);
  const pushHistory = useEditorStore((s) => s.pushHistory);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);
  const setEditorMode = useEditorStore((s) => s.setEditorMode);

  const editorRef = useRef<HTMLDivElement>(null);
  const measurementCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const committedRef = useRef(false);
  const [editorTextSize, setEditorTextSize] = useState({
    width: 0,
    lineCount: 1,
  });
  const [bannerImagesReady, setBannerImagesReady] = useState(
    () => Boolean(getTitleBannerImage("left") && getTitleBannerImage("mid") && getTitleBannerImage("right")),
  );

  const existingLabel =
    editingLabelId && editingLabelId !== "new" && mapState
      ? mapState.textLabels[editingLabelId] ?? null
      : null;

  const style = existingLabel
    ? {
        fontSize: existingLabel.fontSize,
        fontFamily: existingLabel.fontFamily,
        color: existingLabel.color,
        bold: existingLabel.bold,
        italic: existingLabel.italic,
        rotation: existingLabel.rotation ?? 0,
        banner: existingLabel.banner,
      }
    : pendingStyle;

  const doCommit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;

    const el = editorRef.current;
    if (!el || !editPosition) {
      cancelEdit();
      return;
    }

    const text = normalizeTextLabelEditorText(el.innerText || "");
    if (!text.trim()) {
      if (existingLabel) {
        const before: { textLabels: Record<string, MapTextLabel | null> } = {
          textLabels: { [existingLabel.id]: { ...existingLabel } },
        };
        const after: { textLabels: Record<string, MapTextLabel | null> } = {
          textLabels: { [existingLabel.id]: null },
        };
        applyMapChanges(after);
        pushHistory({ before, after });
        getBroadcastMapChange()(after);
      }
      commitEdit();
      return;
    }

    if (existingLabel) {
      const updated: MapTextLabel = {
        ...existingLabel,
        text,
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
        color: style.color,
        bold: style.bold,
        italic: style.italic,
        rotation: style.rotation,
        banner: style.banner,
      };
      const before = { textLabels: { [existingLabel.id]: { ...existingLabel } } };
      const after = { textLabels: { [updated.id]: updated } };
      applyMapChanges(after);
      pushHistory({ before, after });
      getBroadcastMapChange()(after);
    } else {
      const id = crypto.randomUUID();
      const label: MapTextLabel = {
        id,
        x: editPosition.x,
        y: editPosition.y,
        text,
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
        color: style.color,
        bold: style.bold,
        italic: style.italic,
        rotation: style.rotation,
        banner: style.banner,
      };
      const before: { textLabels: Record<string, MapTextLabel | null> } = {
        textLabels: { [id]: null },
      };
      const after = { textLabels: { [id]: label } };
      applyMapChanges(after);
      pushHistory({ before, after });
      getBroadcastMapChange()(after);
      setPendingStyle({
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
        color: style.color,
        bold: style.bold,
        italic: style.italic,
        rotation: style.rotation,
        banner: style.banner,
      });
    }

    commitEdit();
  }, [
    editPosition,
    existingLabel,
    style,
    applyMapChanges,
    pushHistory,
    commitEdit,
    cancelEdit,
    setPendingStyle,
  ]);

  useEffect(() => {
    committedRef.current = false;
  }, [editingLabelId]);

  useEffect(() => {
    if (!editingLabelId) return;

    const el = editorRef.current;
    if (!el) return;

    if (existingLabel) {
      el.innerText = normalizeTextLabelEditorText(existingLabel.text);
    } else {
      el.innerText = "";
    }

    requestAnimationFrame(() => {
      el.focus();
      const selection = window.getSelection();
      if (selection && el.childNodes.length > 0) {
        selection.selectAllChildren(el);
        selection.collapseToEnd();
      }
      // Seed the banner size after the initial content is set, so the banner
      // wraps the existing label text the moment editing begins.
      setEditorTextSize({
        width: el.offsetWidth,
        lineCount: Math.max(1, (el.innerText || "").split("\n").length),
      });
    });
  }, [editingLabelId, existingLabel]);

  useEffect(() => {
    if (!editingLabelId || !style.banner) return;
    if (bannerImagesReady) return;
    let cancelled = false;
    void preloadTitleBannerImages().then(() => {
      if (!cancelled) {
        setBannerImagesReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [editingLabelId, style.banner, bannerImagesReady]);

  useEffect(() => {
    if (!editingLabelId) return;
    const el = editorRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setEditorTextSize((current) => ({
        ...current,
        width: el.offsetWidth,
      }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [editingLabelId]);

  useEffect(() => {
    if (!editingLabelId) return;

    function handleKeyboardCommit() {
      doCommit();
      if (activeTool === "text") {
        setEditorMode("enrich");
        setActiveTool("pointer");
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleKeyboardCommit();
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        handleKeyboardCommit();
        return;
      }
    }

    function handleClickOutside(e: MouseEvent) {
      if (editorRef.current && !editorRef.current.contains(e.target as Node)) {
        const toolbar = (e.target as HTMLElement)?.closest?.(
          "[data-text-style-toolbar]",
        );
        if (toolbar) return;
        doCommit();
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [activeTool, editingLabelId, doCommit, setActiveTool, setEditorMode]);

  if (!editingLabelId || !editPosition) return null;

  const cam = cameraRef.current;
  const canvas = canvasRef.current;
  if (!canvas) return null;

  const screenX = editPosition.x * cam.zoom + canvas.width / 2 + cam.x;
  const screenY = editPosition.y * cam.zoom + canvas.height / 2 + cam.y;
  const scaledFontSize = style.fontSize * cam.zoom;
  const editorHorizontalNudge = scaledFontSize * EDITOR_HORIZONTAL_NUDGE_FACTOR;
  const editorVerticalNudge = scaledFontSize * EDITOR_VERTICAL_NUDGE_FACTOR;
  const rotationDeg = (style.rotation * 180) / Math.PI;

  const editorFontString = buildFontString({ ...style, fontSize: scaledFontSize });
  // Measure text height using the same font-metric path the canvas uses, so
  // the live banner preview matches the rendered banner instead of the CSS
  // line-box height (which is shorter than the painted glyphs for fonts with
  // tall ascenders/descenders, and was making the editor banner look squished).
  let measuredTextHeight = 0;
  if (style.banner && bannerImagesReady && editorTextSize.width > 0) {
    if (!measurementCtxRef.current && typeof document !== "undefined") {
      measurementCtxRef.current =
        document.createElement("canvas").getContext("2d") ?? null;
    }
    if (measurementCtxRef.current) {
      measuredTextHeight = measureTextLabelTextHeight(
        measurementCtxRef.current,
        editorFontString,
        scaledFontSize,
        Array(editorTextSize.lineCount).fill(""),
      );
    }
  }

  const bannerLayout =
    style.banner && bannerImagesReady && editorTextSize.width > 0 && measuredTextHeight > 0
      ? computeTextBannerLayout({
          width: editorTextSize.width,
          height: measuredTextHeight,
          fontSize: scaledFontSize,
          lineCount: editorTextSize.lineCount,
        })
      : null;

  const bannerLeft = bannerLayout ? getTitleBannerImage("left") : undefined;
  const bannerMid = bannerLayout ? getTitleBannerImage("mid") : undefined;
  const bannerRight = bannerLayout ? getTitleBannerImage("right") : undefined;
  const bannerLeftWidth = bannerLayout && bannerLeft ? bannerLeft.naturalWidth * bannerLayout.scale : 0;
  const bannerMidWidth = bannerLayout && bannerMid ? bannerMid.naturalWidth * bannerLayout.scale : 0;
  const bannerRightWidth = bannerLayout && bannerRight ? bannerRight.naturalWidth * bannerLayout.scale : 0;

  return (
    <div
      className="fixed"
      style={{
        // Wrapper sits exactly on the label position so the banner (centred
        // on the wrapper) lines up with the canvas-rendered banner. The
        // editor-text glyph nudge is applied to the contentEditable below
        // instead of the wrapper, so it doesn't drag the banner with it.
        left: screenX,
        top: screenY,
        transform: `translate(-50%, -50%) rotate(${rotationDeg}deg)`,
        zIndex: 50,
      }}
    >
      {bannerLayout && bannerLeft && bannerMid && bannerRight ? (
        <div
          aria-hidden
          data-testid="text-banner-preview"
          className="pointer-events-none absolute left-1/2 top-1/2 flex items-stretch"
          style={{
            width: bannerLayout.width,
            height: bannerLayout.height,
            transform: `translate(-50%, calc(-50% + ${bannerLayout.verticalOffset}px))`,
          }}
        >
          <img
            src={bannerLeft.src}
            alt=""
            draggable={false}
            style={{ width: bannerLeftWidth, height: "100%", display: "block", flex: "0 0 auto" }}
          />
          {Array.from({ length: bannerLayout.midCount }, (_, idx) => (
            <img
              key={`${TITLE_BANNER_FILENAMES.mid}-${idx}`}
              src={bannerMid.src}
              alt=""
              draggable={false}
              style={{ width: bannerMidWidth, height: "100%", display: "block", flex: "0 0 auto" }}
            />
          ))}
          <img
            src={bannerRight.src}
            alt=""
            draggable={false}
            style={{ width: bannerRightWidth, height: "100%", display: "block", flex: "0 0 auto" }}
          />
        </div>
      ) : null}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-text-label-editor
        onInput={(event) => {
          const target = event.currentTarget;
          const rawText = target.innerText || "";
          const cappedText = enforceTextLabelLineCap(rawText);
          // Only rewrite the DOM when the cap actually trimmed something —
          // otherwise we'd reset the caret on every keystroke (and clobber
          // the trailing newline the browser inserted when the user pressed
          // Enter to start line 2).
          if (rawText !== cappedText) {
            target.innerText = cappedText;
            const selection = window.getSelection();
            if (selection && target.childNodes.length > 0) {
              selection.selectAllChildren(target);
              selection.collapseToEnd();
            }
          }
          setEditorTextSize({
            width: target.offsetWidth,
            lineCount: Math.min(
              MAX_TEXT_LABEL_LINES,
              Math.max(1, cappedText.split("\n").length),
            ),
          });
        }}
        className="relative outline-none whitespace-pre-wrap"
        style={{
          display: "inline-block",
          font: editorFontString,
          color: style.color,
          textAlign: "center",
          textShadow: "0 1px 4px rgba(0, 0, 0, 0.7)",
          margin: 0,
          padding: 0,
          border: 0,
          boxSizing: "border-box",
          minWidth: "2em",
          minHeight: "1em",
          caretColor: style.color,
          lineHeight: 1,
          // Empirically tuned offset that aligns the contentEditable's text
          // glyphs with where the canvas paints the label text. Applied here
          // (not to the wrapper) so it shifts only the text, not the banner.
          left: editorHorizontalNudge,
          top: editorVerticalNudge,
        }}
      />
    </div>
  );
}
